// @atlas/contracts — a `org-structure` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
//
// A SZERVEZETI FA HÁROM SZINTŰ: részleg (department) → csapat (team) → beosztás (position).
//   - a munkatárs KIZÁRÓLAG beosztáshoz kötődik; a csapata és a részlege ebből a láncból származik
//   - a SZÍN a CSAPATON él (a részlegnek nincs színe) — ez a rendszer egyetlen szervezeti szín-forrása
//   - NE keverd a `support-teams` modullal: az az ügyfélszolgálati („support-csapat") kör, nem
//     szervezeti egység; a kettő között semmilyen strukturális kapcsolat nincs
import { z } from 'zod';
import { nullableStr } from '../_shared/schemas.js';
/**
 * Egy szervezeti egységhez rendelt munkatárs (belső identity) — a név-részek a locale-tudatos megjelenítéshez
 * (mint az employees/support-teams). A `teamColor` a tag saját CSAPATÁNAK színe (az avatar-akcenthez).
 * A `SupportTeamMemberSchema` alakjának org-structure klónja.
 */
export const OrgMemberSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    teamColor: z.string().nullable(),
});
/** Egy `workforce/departments` sor a BFF-nézetben (az i18n JSON hu/en mezőkre bontva). SZÍNE NINCS. */
export const OrgDepartmentRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    nameHu: z.string(),
    nameEn: z.string(),
    descriptionHu: z.string().nullable(),
    descriptionEn: z.string().nullable(),
    /**
     * A részleghez tartozó munkatársak — a csapatain ÉS azok beosztásain át aggregálva (két szint mélyen).
     * A „Munkatársak" oszlophoz.
     */
    members: z.array(OrgMemberSchema),
    /**
     * A részlegHEZ TARTOZÓ CSAPATOK — a lokalizált név a lista nyelvén, név szerint rendezve, a csapat
     * színével (a részleg-sor így mutatja a színeit anélkül, hogy saját színe lenne).
     */
    teams: z.array(z.object({ id: z.string(), name: z.string(), color: z.string() })),
    version: z.number(),
});
/** Egy `workforce/teams` sor a BFF-nézetben (a részleg-név feloldva). A SZÍN itt él. */
export const OrgTeamRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    nameHu: z.string(),
    nameEn: z.string(),
    descriptionHu: z.string().nullable(),
    descriptionEn: z.string().nullable(),
    color: z.string(),
    departmentId: z.string().nullable(),
    /** A részleg neve az i18n JSON-ból (a lista nyelvén) — a lista feloldva hozza; feloldatlan → null. */
    departmentName: z.string().nullable(),
    /** A csapathoz tartozó munkatársak (a beosztásaikon át aggregálva) — a „Munkatársak" oszlophoz. */
    members: z.array(OrgMemberSchema),
    /**
     * A csapatHOZ TARTOZÓ BEOSZTÁSOK — a lokalizált név a lista nyelvén, név szerint rendezve. Ez a
     * szervezeti felépítés lényege: melyik csapatban milyen szerepek vannak.
     */
    positions: z.array(z.object({ id: z.string(), name: z.string() })),
    /**
     * A csapat VEZETŐJE (#117) — az eszköz-igény jóváhagyásáról tájékoztatót kap. Nem kötelező; a felület
     * figyelmeztet, ha hiányzik.
     */
    leadIdentityId: z.string().nullable(),
    /** A vezető feloldva (név + avatar) — nem belső / nem feloldható identitásnál `null`. */
    lead: OrgMemberSchema.nullable(),
    version: z.number(),
});
/** Egy `workforce/positions` sor a BFF-nézetben (a csapat- és a részleg-név feloldva). */
export const OrgPositionRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    nameHu: z.string(),
    nameEn: z.string(),
    descriptionHu: z.string().nullable(),
    descriptionEn: z.string().nullable(),
    teamId: z.string().nullable(),
    /** A csapat neve az i18n JSON-ból (a lista nyelvén) — feloldatlan → null. */
    teamName: z.string().nullable(),
    /** A csapat szín-tokenje (a csapat-badge-hez); feloldatlan → null. */
    teamColor: z.string().nullable(),
    /** A csapat RÉSZLEGÉNEK neve (két szint feloldva) — a „hova tartozik" kontextushoz; feloldatlan → null. */
    departmentName: z.string().nullable(),
    /** A beosztáshoz rendelt munkatársak — a „Munkatársak" oszlophoz. */
    members: z.array(OrgMemberSchema),
    version: z.number(),
});
/** Org-lista lekérdezés: szabad-szöveg (név/slug) + szerver-rendezés (sort/order) + offset-lapozás (BFF-szűrt halmaz). */
export const OrgListQuerySchema = z.object({
    q: z.string().optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/**
 * Részleg create/update közös body. A `slug`-ot a BFF generálja a `nameEn`-ből (create; az update stabil
 * marad). A leírás-mezőpár opcionális (üres → null).
 * SZÍN NINCS: a részleg nem hordoz színt — az a csapat szintjén él (`TeamWriteBodySchema.color`).
 */
export const DepartmentWriteBodySchema = z.object({
    nameHu: z.string().trim().min(1).max(255),
    nameEn: z.string().trim().min(1).max(255),
    descriptionHu: nullableStr(4000),
    descriptionEn: nullableStr(4000),
});
/**
 * Csapat create/update közös body. A `departmentId` KÖTELEZŐ (uuid + LÉTEZŐ részleg — a BFF ellenőrzi,
 * feloldatlan → 422): csapat nem létezhet részleg nélkül. A `color` default `gray` (üres string →
 * default). A `slug`-ot a BFF a `nameEn`-ből generálja (create; az update stabil marad).
 */
export const TeamWriteBodySchema = z.object({
    nameHu: z.string().trim().min(1).max(255),
    nameEn: z.string().trim().min(1).max(255),
    color: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), z.string().trim().max(20).default('gray')),
    departmentId: z.string().uuid(),
    descriptionHu: nullableStr(4000),
    descriptionEn: nullableStr(4000),
    /** A csapatvezető (#117) — üres → nincs kijelölve (nem kötelező). */
    leadIdentityId: z.preprocess((v) => (v === '' || v === undefined ? null : v), z.string().uuid().nullable()),
});
/**
 * Beosztás create/update közös body. A `teamId` kötelező (uuid + LÉTEZŐ csapat — a BFF ellenőrzi,
 * feloldatlan → 422). A `slug`-ot a BFF a `nameEn`-ből generálja (create; az update stabil marad).
 * RÉSZLEGET ITT NEM adunk meg: az a csapatból következik (egy igazság-forrás).
 */
export const PositionWriteBodySchema = z.object({
    nameHu: z.string().trim().min(1).max(255),
    nameEn: z.string().trim().min(1).max(255),
    teamId: z.string().uuid(),
    descriptionHu: nullableStr(4000),
    descriptionEn: nullableStr(4000),
});
//# sourceMappingURL=schemas.js.map